import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check } from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useUser } from "../Context/UserContext";
import SolicitarCotizacionModal from "./SolicitarCotizacionModal";

const plans = [
  {
    id: "Gratuita",
    price: "Gratis",
    description: "Para empezar a publicar licitaciones sin fricción.",
    features: [
      "Hasta 2 licitaciones activas al mes",
      "Comparación básica de postulaciones",
      "1 usuario",
    ],
    ctaLabel: "Comenzar gratis",
  },
  {
    id: "Media",
    price: "A medida",
    description: "Para equipos que compran con más frecuencia.",
    features: [
      "Más licitaciones activas simultáneas",
      "Guarda proveedores favoritos",
      "Alertas de nuevas postulaciones",
      "Hasta 3 usuarios",
    ],
    ctaLabel: "Solicitar cotización",
  },
  {
    id: "Pro",
    price: "A medida",
    description: "Para compras recurrentes que necesitan más control.",
    features: [
      "Licitaciones activas ilimitadas",
      "Historial exportable",
      "Búsqueda avanzada de proveedores, incluye filtro por certificados",
      "Soporte prioritario",
      "Más usuarios",
    ],
    ctaLabel: "Solicitar cotización",
    highlight: true,
  },
  {
    id: "Enterprise",
    price: "Cotización personalizada",
    description: "Para compradores con compra recurrente y múltiples licitaciones simultáneas.",
    features: [
      "Todo lo de Pro",
      "Multi-usuario con roles y permisos",
      "Dashboard consolidado de licitaciones",
      "Reportes de ahorro de tiempo y trazabilidad",
      "Gestor de cuenta dedicado",
    ],
    ctaLabel: "Hablar con ventas",
  },
];

const Pricing = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [modalPlan, setModalPlan] = useState(null);

  const handleCta = (plan) => {
    if (plan.id === "Gratuita") {
      navigate(user ? "/company-register" : "/register");
      return;
    }
    setModalPlan(plan.id);
  };

  return (
    <PageLayout>
      <Helmet>
        <title>Precios - Procu</title>
        <meta
          name="description"
          content="Conoce los planes de Procu para gestionar tus licitaciones y encontrar proveedores confiables. Desde autoservicio gratuito hasta soluciones enterprise."
        />
        <meta name="keywords" content="precios, planes, Procu, licitaciones, B2B, enterprise" />
      </Helmet>

      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-foreground">Planes de Procu</h1>
          <p className="mt-2 text-muted-foreground">
            Elige el plan que se ajuste a cómo compra tu empresa. Los planes pagos se
            gestionan por cotización, sin cobro en línea.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`flex flex-col ${plan.highlight ? "border-primary shadow-md" : ""}`}
            >
              <CardHeader>
                {plan.highlight && (
                  <Badge className="w-fit">Más popular</Badge>
                )}
                <CardTitle>{plan.id}</CardTitle>
                <p className="text-2xl font-semibold text-foreground">{plan.price}</p>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="flex flex-col gap-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button
                  className="w-full"
                  variant={plan.highlight ? "default" : "outline"}
                  onClick={() => handleCta(plan)}
                >
                  {plan.ctaLabel}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>

      <SolicitarCotizacionModal
        show={!!modalPlan}
        plan={modalPlan}
        onClose={() => setModalPlan(null)}
      />
    </PageLayout>
  );
};

export default Pricing;
